/* =========================================================
   Fuente de datos contra la API real.

   Estrategia: hidratar y luego leer de memoria.

   Al iniciar sesión se pide `GET /api/bootstrap`, que devuelve todo
   el alcance del usuario en un viaje (189 KB medidos para un Admin
   ETC con el padrón completo). A partir de ahí las lecturas salen de
   memoria, exactamente igual que en la demo — por eso el `DataSource`
   sigue siendo síncrono y ninguna pantalla cambió.

   Las escrituras sí son asíncronas. Se aplican de inmediato en la
   interfaz y se envían al servidor; si el servidor las rechaza, se
   revierte el valor y se avisa con el motivo real (ventana cerrada,
   sin permiso, campo bloqueado). Es preferible a fingir que se
   guardó algo que no se guardó.

   Lo que no debe pasar por esta vía falla de forma explícita en vez de
   quedarse en memoria simulando éxito: ver `removeAssignment`.
   ========================================================= */
import type {
  AppUser, Assignment, ComplianceDoc, DisassocRequest, Instructor, LegalRequest,
  Lang, Notification, Organization, Site,
  UserDraft, UserPatch, UserWriteResult,
} from '@/types';
import type { ComplianceProgressPatch } from './DataSource';
import type { ComplianceRegistry } from '@/types';
import type { MarcasNA } from '@/lib/progreso';
import { ApiError, api } from '@/lib/api';
import { setLanguage } from '@/i18n';
import { MemoryDataSource } from './MemoryDataSource';

interface Bootstrap {
  window: { open: boolean; deadline: string | null };
  scope: { role: string; orgIds: string[]; siteIds: string[] };
  orgs: Organization[];
  sites: Site[];
  instructors: Instructor[];
  assignments: Assignment[];
  /** Solo llega con rol Admin ETC; para el resto es una lista vacía. */
  users: AppUser[];
  disassocRequests: DisassocRequest[];
  legalRequests: LegalRequest[];
  /** Documentos por organización, con el progreso de quien pregunta.
   *  Sin los bytes: esos se piden al abrir cada documento. */
  compliance: ComplianceRegistry;
  /** Campos marcados «no aplica», agrupados por entidad e identificador. */
  na?: MarcasNA;
  /** Idioma guardado en la cuenta. */
  lang?: Lang;
  /** El porcentaje de avance GUARDADO en la base, por organización y sede. */
  progreso?: ProgresoGuardadoMapa;
}

/** Lo que el servidor guarda y devuelve de cada ficha. */
export interface ProgresoGuardado {
  hechos: number;
  total: number;
  pct: number;
  calculadoEn: string;
}
export type ProgresoGuardadoMapa = Record<'organization' | 'site', Record<string, ProgresoGuardado>>;

/** Aviso de que llegó un porcentaje nuevo del servidor: la app lo conecta a
 *  un contador del store para que las pantallas se vuelvan a dibujar. */
let notifyProgreso: () => void = () => {};
export const onProgresoCambiado = (fn: () => void) => {
  notifyProgreso = fn;
};

/** Aviso de escritura fallida hacia la interfaz. Lo conecta la app
 *  para convertirlo en un toast, sin que esta clase dependa del store. */
export type WriteFailure = (message: string) => void;

let notifyFailure: WriteFailure = (m) => console.error('[api]', m);
export const onWriteFailure = (fn: WriteFailure) => {
  notifyFailure = fn;
};

/** Lo que la API acepta de una cuenta, y nada más.
 *
 *  La pantalla manda la fila entera —incluidos `id`, `lastActive` y la
 *  clave interna de la tabla—, y campos como la última actividad los
 *  calcula el servidor. Enviarlos no serviría de nada y ensuciaría la
 *  auditoría. */
function toPayload(u: UserDraft | UserPatch) {
  const out: Record<string, unknown> = {};
  const copiar = (k: 'FirstName' | 'LastName' | 'Email' | 'role' | 'status' | 'orgId' | 'siteId') => {
    if (u[k] !== undefined) out[k] = u[k];
  };
  (['FirstName', 'LastName', 'Email', 'role', 'status', 'orgId', 'siteId'] as const).forEach(copiar);
  if (u.password) out.password = u.password;
  if (u.auth) out.auth = u.auth;
  return out;
}

/** Lo que el servidor quiso contar de la última publicación: a cuánta
 *  gente se avisó, o que no había a quién. La pantalla lo lee justo
 *  después de publicar. */
let ultimasNotas: string[] = [];
export const notasDeLaUltimaPublicacion = () => {
  const n = ultimasNotas;
  ultimasNotas = [];
  return n;
};

/** Igualdad de valor, no de referencia. Las listas —industria, portafolio,
 *  entorno— vuelven del servidor como arrays nuevos: con `===` parecían
 *  cambiadas siempre y viajaban en cada guardado aunque nadie las tocara. */
function mismoValor(a: unknown, b: unknown): boolean {
  if (Array.isArray(a) && Array.isArray(b)) {
    return a.length === b.length && a.every((x, i) => x === b[i]);
  }
  return a === b;
}

export class ApiDataSource extends MemoryDataSource {
  /** Estado de la ventana de actualización, tal como lo dio el servidor. */
  window: { open: boolean; deadline: string | null } = { open: false, deadline: null };
  hydrated = false;

  /** El porcentaje de avance tal como está guardado en la base. Las
   *  pantallas enseñan esto y nada más: el navegador no lo calcula. */
  private progreso: ProgresoGuardadoMapa = { organization: {}, site: {} };
  override readonly guardaProgreso = true;

  constructor(readonly baseUrl: string = '') {
    super();
  }

  override progresoGuardado(entidad: 'organization' | 'site', id: string) {
    return this.progreso[entidad][id] ?? null;
  }

  /** Mezcla lo que devolvió el servidor y avisa a las pantallas. */
  private aplicarProgreso(p: ProgresoGuardadoMapa | undefined) {
    if (!p) return;
    Object.assign(this.progreso.organization, p.organization);
    Object.assign(this.progreso.site, p.site);
    notifyProgreso();
  }

  /** Vuelve a leer lo guardado. Después de las escrituras que no lo
   *  devuelven en su respuesta: afiliar, autorizar una baja, aprobar un
   *  contacto legal. */
  async refrescarProgreso(): Promise<void> {
    const r = await api.get<{ progreso: ProgresoGuardadoMapa }>('/api/progreso');
    this.aplicarProgreso(r.progreso);
  }

  /** Carga el alcance del usuario. Se llama justo después del login y
   *  al recuperar una sesión existente. */
  async hydrate(): Promise<void> {
    const data = await api.get<Bootstrap>('/api/bootstrap');
    this.orgs = data.orgs;
    this.sites = data.sites;
    this.instructors = data.instructors;
    this.assignments = data.assignments;
    this.appUsers = data.users ?? [];
    this.disassocReqs = data.disassocRequests ?? [];
    this.legalReqs = data.legalRequests ?? [];
    this.compliance = data.compliance ?? {};
    this.na = data.na ?? { organization: {}, site: {}, instructor: {} };
    this.progreso = data.progreso ?? { organization: {}, site: {} };
    // El compliance lo cumple el administrador del socio y nadie más.
    this.esCuentaDelSocio = data.scope?.role === 'PARTNER_MGR';
    this.window = data.window;
    this.hydrated = true;
    // El idioma vive en la cuenta: aplicarlo aquí es lo que hace que la
    // misma persona entrando desde otro equipo lo encuentre como lo dejó.
    if (data.lang) setLanguage(data.lang, { persistir: false });
    // no bloquea la entrada: si falla, la campana sale vacía y se vuelve a
    // leer en cuanto se abra
    void this.refreshNotifications().catch(() => {});
  }

  /** Guarda el idioma en la cuenta. No es optimista al revés: la pantalla
   *  ya cambió de idioma antes de llamar aquí, y si el guardado falla lo
   *  peor que pasa es que la próxima sesión desde otro equipo salga en el
   *  anterior. No merece un aviso de error encima de la pantalla. */
  override async setLang(lang: Lang): Promise<void> {
    await api.patch('/api/me/lang', { lang });
  }

  /** Abre o cierra la ventana, o mueve la fecha límite. Solo Admin ETC.
   *
   *  No es optimista: mientras la ventana está cerrada nadie escribe, así
   *  que dar por hecho un cambio que el servidor no aplicó dejaría al
   *  administrador creyendo que cerró algo que sigue abierto. */
  async setWindow(next: { open?: boolean; deadline?: string }) {
    const { window } = await api.patch<{ window: { open: boolean; deadline: string | null } }>(
      '/api/window', next,
    );
    this.window = window;
    return window;
  }

  /* ---------------- escrituras ---------------- */

  /** Campos que viajan en el registro pero no son columnas suyas: el site
   *  los recibe del JOIN con su organización. Enviarlos sería un error. */
  private static readonly DERIVED: Record<string, readonly string[]> = {
    sites: ['OrgName', 'OrgWebAddress', 'PrimarySiteId'],
  };

  /** Aplica el cambio en memoria, lo manda al servidor y lo revierte si
   *  el servidor lo rechaza.
   *
   *  Las pantallas guardan el registro entero, no solo lo editado, así que
   *  aquí se compara contra el valor actual y se envía únicamente lo que
   *  de verdad cambió. Eso evita mandar identificadores y campos derivados
   *  —que el servidor rechaza, y con razón— y deja el registro de auditoría
   *  limpio: solo lo que el usuario tocó. */
  private push<T extends Record<string, unknown>>(
    entity: 'organizations' | 'sites' | 'instructors',
    id: string,
    target: T | undefined,
    patch: Partial<T>,
  ) {
    if (!target) return;
    const derived = ApiDataSource.DERIVED[entity] ?? [];
    const changed: Partial<T> = {};
    const before: Partial<T> = {};
    for (const k of Object.keys(patch) as (keyof T & string)[]) {
      if (derived.includes(k)) continue;
      // La fecha de actualización la pone el servidor en cada escritura. Si
      // una pantalla guarda un borrador viejo, aquí llega con otra fecha, y
      // mandarla hacía que el servidor rechazara el cambio ENTERO.
      if (k === 'LastUpdated' || k === 'LastUpdatedAt') continue;
      if (mismoValor(patch[k], target[k])) continue;
      changed[k] = patch[k];
      before[k] = target[k];
    }

    Object.assign(target, patch);
    if (!Object.keys(changed).length) return;

    api
      .patch<{ record: T; progreso?: ProgresoGuardadoMapa }>(
        `/api/${entity}/${encodeURIComponent(id)}`, { fields: changed },
      )
      .then(({ record, progreso }) => {
        // el servidor sella LastUpdated y puede normalizar valores
        Object.assign(target, record);
        // y devuelve el porcentaje que acaba de guardar
        this.aplicarProgreso(progreso);
      })
      .catch((err: unknown) => {
        Object.assign(target, before);
        notifyFailure(
          err instanceof ApiError ? err.message : 'No se pudo guardar el cambio',
        );
      });
  }

  override updateOrg(orgId: string, patch: Partial<Organization>) {
    this.push('organizations', orgId, this.getOrg(orgId), patch);
  }

  override updateSite(siteId: string, patch: Partial<Site>) {
    this.push('sites', siteId, this.getSite(siteId), patch);
  }

  override updateInstructor(instructorId: string, patch: Partial<Instructor>) {
    this.push('instructors', instructorId, this.getInstructor(instructorId), patch);
  }

  /* ---------------- cuentas del portal ---------------- */

  /* A diferencia de la edición de campos, esto NO es optimista. Dar de
     alta una cuenta con una contraseña que el administrador acaba de
     copiar y entregar, para después descubrir que el servidor la
     rechazó, es peor que esperar medio segundo. */

  override async createUser(obj: UserDraft): Promise<UserWriteResult> {
    const res = await api.post<UserWriteResult>('/api/users', toPayload(obj));
    this.appUsers.unshift(res.record);
    return { record: res.record, notes: res.notes ?? [] };
  }

  override async updateUser(id: string, patch: UserPatch): Promise<UserWriteResult> {
    const res = await api.patch<UserWriteResult>(
      `/api/users/${encodeURIComponent(id)}`, toPayload(patch),
    );
    // el servidor manda la fila releída: manda ella, no lo que se envió
    const i = this.appUsers.findIndex((u) => u.id === id);
    if (i >= 0) this.appUsers[i] = res.record;
    else this.appUsers.unshift(res.record);
    return { record: res.record, notes: res.notes ?? [] };
  }

  /* ---------------- solicitudes y afiliaciones ---------------- */

  /* Estas sí son optimistas, como la edición de campos: la pantalla
     responde al instante y, si el servidor rechaza, se deshace y se
     explica. Deshacer aquí es rehacer el estado en memoria, así que cada
     método guarda antes lo que va a cambiar. */

  /** Lanza la petición y, si falla, devuelve la memoria a como estaba. */
  private mandar(promesa: Promise<unknown>, deshacer: () => void) {
    promesa
      // lo que cambió en el servidor puede mover porcentajes: se relee lo guardado
      .then(() => this.refrescarProgreso().catch(() => {}))
      .catch((err: unknown) => {
        deshacer();
        notifyFailure(err instanceof ApiError ? err.message : 'No se pudo completar la operación');
      });
  }

  /* ---------------- campana ---------------- */

  /* Los avisos que importan son los que manda OTRA persona: una solicitud
     que un socio acaba de enviar, o la resolución que el Admin ETC acaba
     de tomar. Eso no puede vivir en el navegador de nadie, así que la cola
     viene del servidor. Los avisos flotantes de lo que uno mismo hace
     siguen siendo eso, avisos flotantes: no entran aquí. */

  override async refreshNotifications(): Promise<void> {
    const r = await api.get<{ notifications: Notification[] }>('/api/notifications');
    this.notifications = r.notifications ?? [];
  }

  /** En modo API la cola es del servidor: un aviso local solo duraría hasta
   *  recargar y se mezclaría con los de verdad. */
  override addNotification(): void {}

  override markAllNotificationsRead() {
    this.notifications.forEach((n) => { n.read = true; });
    api.post('/api/notifications/read').catch(() => {
      /* se vuelven a marcar en la siguiente lectura */
    });
  }

  override clearNotifications() {
    this.notifications = [];
    api.del('/api/notifications').catch((err: unknown) => {
      notifyFailure(err instanceof ApiError ? err.message : 'No se pudo vaciar la cola');
    });
  }

  /** Relee las dos bandejas. Es barato comparado con rehidratar el padrón,
   *  y es lo que hace que una solicitud recién enviada por un socio le
   *  aparezca al Admin ETC sin tener que volver a entrar. */
  override async refreshRequests(): Promise<void> {
    const r = await api.get<{ disassocRequests: DisassocRequest[]; legalRequests: LegalRequest[] }>(
      '/api/requests',
    );
    this.disassocReqs = r.disassocRequests ?? [];
    this.legalReqs = r.legalRequests ?? [];
  }

  override requestDisassociation(req: Omit<DisassocRequest, 'date'>) {
    if (this.hasDisassocRequest(req.InstructorId, req.SiteId)) return;
    super.requestDisassociation(req);
    this.mandar(
      api.post('/api/disassociations', {
        instructorId: req.InstructorId, siteId: req.SiteId, reason: req.reason || undefined,
      }),
      () => super.cancelDisassociation(req.InstructorId, req.SiteId),
    );
  }

  override cancelDisassociation(instructorId: string, siteId: string) {
    const previa = this.disassocReqs.find(
      (r) => r.InstructorId === instructorId && r.SiteId === siteId,
    );
    if (!previa) return;
    super.cancelDisassociation(instructorId, siteId);
    this.mandar(
      api.del('/api/disassociations?' + new URLSearchParams({ instructorId, siteId })),
      () => { this.disassocReqs.push(previa); },
    );
  }

  override rejectDisassociation(instructorId: string, siteId: string, reason: string) {
    const previa = this.disassocReqs.find(
      (r) => r.InstructorId === instructorId && r.SiteId === siteId,
    );
    if (!previa) return;
    super.rejectDisassociation(instructorId, siteId, reason);
    this.mandar(
      api.post('/api/disassociations/reject', { instructorId, siteId, rejectReason: reason }),
      () => { this.disassocReqs.push(previa); },
    );
  }

  override authorizeDisassociation(instructorId: string, siteId: string): number {
    const asignacion = this.assignments.find(
      (a) => a.InstructorId === instructorId && a.SiteId === siteId,
    );
    const solicitud = this.disassocReqs.find(
      (r) => r.InstructorId === instructorId && r.SiteId === siteId,
    );
    const eraDesasociado = this.getInstructor(instructorId)?.disassociated;

    const quedan = super.authorizeDisassociation(instructorId, siteId);
    this.mandar(
      api.post('/api/disassociations/authorize', { instructorId, siteId }),
      () => {
        if (asignacion) this.assignments.push(asignacion);
        if (solicitud) this.disassocReqs.push(solicitud);
        const i = this.getInstructor(instructorId);
        if (i && eraDesasociado !== undefined) i.disassociated = eraDesasociado;
      },
    );
    return quedan;
  }

  override addAssignment(instructorId: string, siteId: string, primaryRoleId?: string) {
    const eraDesasociado = this.getInstructor(instructorId)?.disassociated;
    const creada = super.addAssignment(instructorId, siteId, primaryRoleId);
    if (!creada) return null;
    this.mandar(
      api.post('/api/assignments', { instructorId, siteId, primaryRoleId }),
      () => {
        this.assignments = this.assignments.filter((a) => a !== creada);
        const i = this.getInstructor(instructorId);
        if (i && eraDesasociado !== undefined) i.disassociated = eraDesasociado;
      },
    );
    return creada;
  }

  override deactivateInstructor(instructorId: string) {
    const i = this.getInstructor(instructorId);
    if (!i) return;
    const antes = i.active;
    super.deactivateInstructor(instructorId);
    this.mandar(
      api.post(`/api/instructors/${encodeURIComponent(instructorId)}/deactivate`),
      () => { i.active = antes; },
    );
  }

  /** No es optimista, a diferencia del resto: el formulario se queda
   *  abierto hasta que el servidor confirma. Cerrarlo y avisar después del
   *  fallo obligaba al socio a volver a escribirlo todo sin saber qué. */
  override async addLegalRequest(
    req: Omit<LegalRequest, 'id' | 'date' | 'status'>, carta?: File | null,
  ): Promise<LegalRequest> {
    // Multipart aunque no haya carta: un solo camino que mantener, y así
    // adjuntar o no adjuntar no cambia lo que pasa si el servidor rechaza.
    const form = new FormData();
    form.append('OrgId', req.OrgId);
    form.append('first', req.first);
    form.append('last', req.last);
    form.append('email', req.email);
    form.append('phone', req.phone ?? '');
    if (carta) form.append('doc', carta, carta.name);

    const { record } = await api.upload<{ record: LegalRequest }>('/api/legal-requests', form);
    this.legalReqs.unshift(record);
    return record;
  }

  /** Descarga la carta de nombramiento. Como el compliance: los bytes no
   *  viajan en la hidratación, se piden cuando alguien la abre. */
  override async loadLegalDoc(id: string): Promise<string> {
    const ya = this.archivos.get(`legal:${id}`);
    if (ya) return ya;
    const bytes = await api.bytes(`/api/legal-requests/${encodeURIComponent(id)}/file`);
    const url = URL.createObjectURL(bytes);
    this.archivos.set(`legal:${id}`, url);
    return url;
  }

  override resolveLegalRequest(id: string, status: 'approved' | 'rejected', rejectReason?: string) {
    const previa = this.legalReqs.find((r) => r.id === id);
    if (!previa) return;
    const antes = { ...previa };
    super.resolveLegalRequest(id, status, rejectReason);
    api
      .patch<{ record: LegalRequest; org: Organization }>(
        `/api/legal-requests/${encodeURIComponent(id)}`, { status, rejectReason },
      )
      .then(({ record, org }) => {
        const n = this.legalReqs.findIndex((r) => r.id === id);
        if (n >= 0) this.legalReqs[n] = record;
        // aprobar cambia el contacto legal de la organización: es la única
        // vía que lo toca, así que la ficha en memoria se pone al día
        const o = this.getOrg(org.OrgId);
        if (o) Object.assign(o, org);
      })
      .catch((err: unknown) => {
        const n = this.legalReqs.findIndex((r) => r.id === id);
        if (n >= 0) this.legalReqs[n] = antes;
        notifyFailure(err instanceof ApiError ? err.message : 'No se pudo resolver la solicitud');
      });
  }

  /* ---------------- documentos de compliance ---------------- */

  /** URLs `blob:` ya creadas, por documento. Volver a abrir el mismo
   *  documento no lo baja otra vez; se sueltan al retirarlo. */
  private archivos = new Map<string, string>();

  override async loadComplianceFile(orgId: string, docId: string): Promise<string> {
    const ya = this.archivos.get(docId);
    if (ya) return ya;
    const bytes = await api.bytes(`/api/compliance/${encodeURIComponent(docId)}/file`);
    const url = URL.createObjectURL(bytes);
    this.archivos.set(docId, url);
    return url;
  }

  override async addComplianceDoc(orgId: string, file: File, title?: string): Promise<ComplianceDoc> {
    const form = new FormData();
    if (title) form.append('title', title);
    form.append('file', file, file.name);
    const { record, notes, progreso } = await api.upload<{
      orgId: string; record: ComplianceDoc; notes?: string[]; progreso?: ProgresoGuardadoMapa;
    }>(`/api/compliance/${encodeURIComponent(orgId)}`, form);
    (this.compliance[orgId] ??= []).unshift(record);
    this.aplicarProgreso(progreso);
    ultimasNotas = notes ?? [];
    return record;
  }

  /* ---------------- «no aplica» ---------------- */

  /** Excusa un campo o le retira la excusa.
   *
   *  Optimista como el resto: la casilla y el porcentaje responden al
   *  instante y, si el servidor rechaza —proceso cerrado, campo obligatorio,
   *  ficha fuera de alcance—, se deshace y se explica el motivo real. */
  override async setNA(
    entidad: keyof MarcasNA, id: string, campo: string, marcado: boolean,
  ): Promise<void> {
    const deshacer = this.aplicarNA(entidad, id, campo, marcado);
    this.mandar(
      api.put(
        `/api/na/${entidad}/${encodeURIComponent(id)}/${encodeURIComponent(campo)}`,
        { marcado },
      ),
      deshacer,
    );
  }

  override removeComplianceDoc(orgId: string, docId: string) {
    const docs = this.compliance[orgId] ?? [];
    const previo = docs.find((d) => d.id === docId);
    if (!previo) return;
    super.removeComplianceDoc(orgId, docId);
    const url = this.archivos.get(docId);
    if (url) { URL.revokeObjectURL(url); this.archivos.delete(docId); }
    this.mandar(
      api.del(`/api/compliance/${encodeURIComponent(docId)}`),
      () => { (this.compliance[orgId] ??= []).unshift(previo); },
    );
  }

  /** El avance de lectura sí es optimista: se manda en cada página que
   *  pasa el lector, y perder una marca por un corte de red no vale una
   *  pantalla bloqueada. La respuesta del servidor manda y corrige. */
  override setComplianceProgress(
    orgId: string, docId: string, patch: ComplianceProgressPatch = {},
  ): ComplianceDoc | null {
    const doc = super.setComplianceProgress(orgId, docId, patch);
    if (!doc) return null;
    api
      .patch<{
        pages: number[]; pct: number; verifiedAt: string | null; history: string[];
        cumplidoPorElSocio: boolean; verificadoPorElSocio: string | null;
        verificadoPorElSocioEn: string | null; progreso?: ProgresoGuardadoMapa;
      }>(
        `/api/compliance/${encodeURIComponent(docId)}/progress`, patch,
      )
      .then((r) => {
        doc.pages = r.pages;
        doc.pct = r.pct;
        doc.verifiedAt = r.verifiedAt;
        doc.verifiedDate = r.verifiedAt ? r.verifiedAt.slice(0, 10) : null;
        doc.history = r.history;
        // el estado del socio, como quedó en la base
        doc.cumplidoPorElSocio = r.cumplidoPorElSocio;
        doc.verificadoPorElSocio = r.verificadoPorElSocio;
        doc.verificadoPorElSocioEn = r.verificadoPorElSocioEn;
        this.aplicarProgreso(r.progreso);
      })
      .catch((err: unknown) => {
        notifyFailure(
          err instanceof ApiError ? err.message : 'No se pudo guardar tu avance de lectura',
        );
      });
    return doc;
  }

  /** Quitar una afiliación a mano, sin pasar por la solicitud.
   *
   *  Ninguna pantalla lo ofrece, y es a propósito: una desasociación la
   *  pide el socio y la autoriza ETC, y ese camino está en
   *  `authorizeDisassociation`. Existe porque la interfaz lo declara.
   *  Falla en vez de heredar la versión de memoria, que escribiría solo
   *  en el navegador y aparentaría haber funcionado. */
  override removeAssignment(): number {
    const mensaje =
      'Quitar a un instructor de un site no se hace por esta vía: tiene que '
      + 'pasar por una solicitud de desasociación que autorice ETC.';
    notifyFailure(mensaje);
    throw new Error(mensaje);
  }
}
